/**
 * Refresh Wallet UseCase
 *
 * Fetches the latest wallet from Firestore and persists it to SecureStorage.
 * The write triggers eventBus → WalletListener → Redux global state update.
 *
 * Used after transactions that modify the wallet server-side (e.g. unlock content,
 * send gift) so the local balance stays in sync.
 */
import { SyncService } from '@/domain/services/sync/syncService';
import { WalletRemapper } from '@/domain/remappers/wallet/walletRemapper';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class RefreshWalletUseCase {
  private readonly syncService: SyncService;
  private readonly storage = SecureStorageWebCrypto.getInstance();

  constructor(syncService: SyncService = new SyncService()) {
    this.syncService = syncService;
  }

  async execute(): Promise<void> {
    try {
      const walletDto = await this.syncService.getWallet();
      if (walletDto) {
        const walletStorage = WalletRemapper.toStorage(walletDto);
        await this.storage.setEncrypted(SECURE_STORAGE_KEYS.WALLET, walletStorage);
      }
    } catch (error) {
      appLogger.error('Failed to refresh wallet', 'RefreshWalletUseCase.execute', { error: error as Error });
    }
  }
}
